import helpers from "@/helpers.js"
import { storeTableData } from "@/services/chatTableStorage.js"

const safeParseJson = (data) => {
  if (data === null || data === undefined) { return {} }
  if (typeof data === "string") {
    return JSON.parse(data)
  } else if (typeof data === "object") {
    return data
  } else {
    return {}
  }
}

const prepMessage = (message) => {
  try {
    let history = []
    if (message.statusHistory) {
      if (typeof message.statusHistory === "string") {
        history = JSON.parse(message.statusHistory).history
      } else {
        history = message.statusHistory.history
      }
    }

    delete message.statusHistory
    let meta = safeParseJson(message.meta)

    const workings = meta.tool_summary
    const toolHistory = meta.history || []

    let data = safeParseJson(message.data)
    if (Object.keys(data).indexOf("stores") > -1) {
      meta = data
      data = safeParseJson(message.text)
      message.text = ""
    }
    if (!Array.isArray(data) && typeof data === "object") {
      const keys = Object.keys(data)
      const updatedData = keys.map((k) => {
        return [k, data[k]]
      })
      data = updatedData
    }

    let display
    if (["text", "table", "chart"].indexOf(message.display) > -1) {
      display = { type: message.display }
    } else {
      if (typeof message.display === "string") {
        display = safeParseJson(message.display)
        if (typeof display === "string") {
          display = safeParseJson(display)
        }
      } else {
        display = message.display
      }
    }
    // Ensure display.type is always valid - missing/invalid causes ChatMessage recursion
    if (!display || !display.type || ["text", "table", "chart"].indexOf(display.type) < 0) {
      display = { type: "text" }
    }

    return Object.assign(message, { data, history, meta, display, workings, toolHistory })
  } catch (e) {
    console.error("Unable to parse message data", e)
    return {}
  }
  // return message
}

/**
 * Extract header metadata from table data.
 * Union field names across rows — unset columns (e.g. vor_date) are often omitted from
 * early rows, and headers built from row 0 alone would drop them from the GUI table.
 * Mirrors ChatMessageTable's headerValues logic for field exclusion.
 */
function shouldExcludeExportField (fieldName) {
  if (!fieldName) return true
  if (fieldName === "_title") return true
  if (fieldName === "id") return true
  if (fieldName === "case_template_id" || fieldName === "case_type_id") return true
  const isTopLevel = fieldName.indexOf(".") === -1
  if (isTopLevel && fieldName.endsWith("_ref")) {
    if (fieldName === "claim_ref") return false
    return true
  }
  if (isTopLevel && fieldName.endsWith("_id")) return true
  return false
}

function extractHeadersFromRows (rows) {
  if (!Array.isArray(rows) || rows.length === 0) return []
  const values = []
  const addField = (name, field) => {
    if (!shouldExcludeExportField(name) && values.every(v => v.name !== name)) {
      values.push({ name, type: field.data_type, displayName: field.display_name })
    }
  }
  rows.forEach((row) => {
    if (!Array.isArray(row)) return
    row.forEach((field) => {
      if (Array.isArray(field.field_name)) {
        field.field_name.forEach((f) => addField(f, field))
      } else {
        addField(field.field_name, field)
      }
    })
  })
  return values
}

/**
 * Async prep for store: runs prepMessage, then offloads large table data to IndexedDB.
 * Use this in actions before committing messages.
 */
export async function prepMessageForStore (message) {
  const prepped = prepMessage(Object.assign({}, message))
  if (prepped.display && prepped.display.type === "table" && Array.isArray(prepped.data)) {
    try {
      const headers = extractHeadersFromRows(prepped.data)
      const hasIdField = prepped.data.length > 0 &&
        prepped.data[0].some((f) => (Array.isArray(f.field_name) ? f.field_name[0] : f.field_name) === 'id')
      const ref = await storeTableData(String(prepped.id), prepped.data, headers, hasIdField)
      prepped.data = ref
    } catch (e) {
      console.warn("Failed to store table in IndexedDB, using in-memory:", e)
    }
  }
  prepped.__prepped = true
  return prepped
}

const formatField = (field) => {
  if (!field || field.value === null || field.value === undefined) {
    return ""
  }

  // Nested entity / relation arrays — format each entry and join
  if (Array.isArray(field.value)) {
    return field.value.map((item) => {
      if (item === null || item === undefined) return ""
      if (typeof item === "object") {
        return formatField({ value: item, data_type: field.data_type, field_name: field.field_name, symbol: field.symbol })
      }
      return String(item)
    }).filter((v) => v !== "").join("; ")
  }

  // Check if value is a nested object (relation field)
  if (typeof field.value === "object" && field.value !== null) {
    // Prefer title / _title for relation-like objects (avoids "[object Object]" in CSV/export)
    if (field.value.title) {
      return field.value.title
    }
    if (field.value._title) {
      return field.value._title
    }
    // Fallback: try to find any string value that might be a title
    const stringValues = Object.values(field.value).filter(v => typeof v === "string" && v.length > 0)
    if (stringValues.length > 0) {
      return stringValues[0]
    }
    // Last resort: return id if available
    if (field.value.id) {
      return `#${field.value.id}`
    }
    // If we can't find a display value, return empty string (the object will still be stored for click handling)
    return ""
  }

  if (field.data_type === "currency") {
    return helpers.formatCurrency(field.value, field.symbol || "£")
  }
  if (field.data_type === "days" || field.data_type === "date_span") {
    const num = parseFloat(field.value)
    if (isNaN(num)) return ""
    return Math.ceil(num)
  }
  if (field.data_type === "date_part") {
    // Date variation fields (__month, __year, etc.) store integers
    if (field.value == null) return ""
    if (field.field_name && String(field.field_name).includes("__month") && field.value >= 1 && field.value <= 12) {
      const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
      return monthNames[field.value - 1]
    }
    return String(field.value)
  }
  if (field.data_type === "date" || field.data_type === "datetime") {
    if (field.value == null || field.value === "") return ""
    const format = field.format || "DD/MM/YYYY h:mma"
    return helpers.formatDateTime(new Date(field.value), format)
  }
  if (field.data_type === "number") {
    return helpers.formatNumber(field.value)
  }

  return parseNumberIfNeeded(field.value)
}

const parseNumberIfNeeded = (input) => {
  // Trim the input
  if (input === undefined || input === null) {
    return ""
  }
  const trimmed = input.toString().trim()

  // Regular expression to check for valid float format
  const floatPattern = /^-?\d{1,3}(,\d{3})*(\.\d+)?$|^-?\d+(\.\d+)?$/

  // If it matches a valid number pattern, attempt to parse
  if (floatPattern.test(trimmed)) {
    const parsed = parseFloat(trimmed.replace(/,/g, "")) // Remove commas before parsing
    return isNaN(parsed) ? trimmed : parsed
  }

  // Otherwise, return the original string, trimmed
  return trimmed
}

/**
 * Optimistic user message shown immediately on send, before the WebSocket delivers the real row.
 */
export function buildPendingUserMessage (text, author, clientId) {
  const trimmed = (text || "").trim()
  const pendingId = `pending-${clientId}`
  const base = prepMessage({
    id: pendingId,
    text: trimmed,
    display: JSON.stringify({ type: "text", sub_type: "", meta: "" }),
    statusHistory: JSON.stringify({ history: [] }),
    author: {
      id: author.id,
      email: author.email,
      displayName: author.displayName || author.email,
      isBot: false,
    },
    data: "[]",
    meta: "{}",
    createdAt: new Date().toISOString(),
    error: false,
  })
  return Object.assign(base, {
    id: pendingId,
    clientId,
    __pending: true,
    __prepped: true,
  })
}

export { prepMessage, formatField, extractHeadersFromRows, shouldExcludeExportField }
